<template>
  <div>
    <InputLabelBase
      v-if="label"
      :label="label"
      :tool-tip="toolTip"
      :helper-text="helperText"
      :description="description"
      :small="small"
    />
    <div class="group relative">
      <div
        v-if="iconOverride || icon"
        class="pointer-events-none absolute inset-y-0 flex items-center px-3"
        :class="iconLeft ? 'left-0' : 'right-0'"
      >
        <component
          :is="iconOverride || icon || ''"
          class="size-4 fill-grey-600 group-focus-within:fill-grey-900 group-active:fill-grey-900"
          aria-hidden="true"
        />
      </div>
      <button
        v-if="type === 'password'"
        class="absolute inset-y-0 flex items-center px-3"
        :class="[iconLeft ? 'left-0 pl-3' : 'right-0 pr-3']"
        type="button"
        @mousedown.prevent="focusInput()"
        @mouseup.prevent="focusInput()"
      >
        <component
          :is="
            inputTypeOverride === 'password'
              ? IconToggleHidden
              : IconToggleVisible
          "
          class="size-4 fill-grey-600 group-focus-within:fill-grey-900 group-active:fill-grey-900"
        />
      </button>
      <input
        :id="label ? labelCleanUp(label) : ''"
        ref="inputBase"
        v-model="model"
        :type="inputTypeOverride ? inputTypeOverride : type"
        :name="label ? labelCleanUp(label) : ''"
        v-bind="inputAttributes"
        :max="dateMax"
        :class="[
          ringStyle,
          small ? 'py-1.5 text-sm' : 'py-2 text-base',
          icon && iconLeft
            ? 'pl-10 pr-3'
            : icon && !iconLeft
              ? 'pl-3 pr-10'
              : 'px-3',

          style?.roundedFull ? 'rounded-full' : 'rounded-md',
        ]"
        :disabled="disabled"
        class="block w-full border-none bg-white shadow-inner outline-none ring-1 ring-inset placeholder:text-grey-500 focus:text-grey-900 focus:ring-2 active:text-grey-900 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
    <p
      v-if="error || hint"
      class="mb-0 mt-1 text-sm"
      :class="error ? 'text-rose-700' : 'text-grey-600'"
    >
      {{ error || hint }}
    </p>
  </div>
</template>

<script setup lang="ts">
import IconEmail from "@/components/icon/Email.vue";
import IconPhone from "@/components/icon/Phone.vue";
import IconToggleHidden from "@/components/icon/toggle/Hidden.vue";
import IconToggleVisible from "@/components/icon/toggle/Visible.vue";

import { labelCleanUp } from "@/utils/formValidation";

const model = defineModel<string | number>();
const props = defineProps<{
  small?: boolean;
  icon?: Component | string;
  iconLeft?: boolean;
  placeholder?: string;
  label?: string;
  toolTip?: string;
  helperText?: string;
  description?: string;
  maxChars?: number;
  hint?: string;
  error?: string;
  dateMax?: string;
  dateMin?: string;
  type: "text" | "password" | "email" | "number" | "date" | "tel" | "search";
  disabled?: boolean;
  required?: boolean;
  hideArrow?: boolean;
  style?: { roundedFull?: boolean; borderLight?: boolean; onDark?: boolean };
}>();

const iconOverride = shallowRef<Component>();
const helperTextOverride = ref("");
const inputTypeOverride = ref("");
const inputBase = ref(null);
const focusInput = () => {
  inputTypeOverride.value =
    inputTypeOverride.value === "text" ? "password" : "text";
  if (inputBase.value !== null)
    return (inputBase.value as HTMLInputElement).focus();
};

const type = computed(() => props.type);
const error = computed(() => props.error);

// Only pass HTML attributes to the input if they're relevant to the input type
const inputAttributes = computed(() => {
  if (props.type === "date") {
    return {
      max: props.dateMax,
      min: props.dateMin,
      required: props.required,
    };
  }

  return {
    placeholder: props.placeholder,
    maxlength: props.maxChars ?? 200,
    required: props.required,
  };
});

const ringStyle = computed(() => {
  if (props.error) {
    return "ring-rose-700";
  } else if (props.style?.borderLight && props.style?.onDark) {
    return "ring-grey-900/10 focus:ring-jag-950";
  } else if (props.style?.borderLight && !props.style?.onDark) {
    return "ring-grey-900/10 focus:ring-green-700";
  } else if (props.style?.onDark) {
    return "ring-grey-400 focus:ring-jag-950";
  } else {
    return "ring-grey-400 focus:ring-green-700";
  }
});

switch (type.value) {
  case "date":
    helperTextOverride.value = "DD/MM/YYYY";
    break;
  case "password":
    inputTypeOverride.value = "password";
    break;
  case "email":
    iconOverride.value = IconEmail;
    break;
  case "tel":
    iconOverride.value = IconPhone;
    break;
  default:
    break;
}
</script>

<style scoped>
.hide-input-arrow::-webkit-inner-spin-button,
.hide-input-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.hide-input-arrow {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
